import Konva from 'konva'

export const GUID_LINE = 'guid-line'
export const GUIDELINE_OFFSET = 10

// 如果要开启参考线，在元件上加上SNAP_NODE的name就可以了
export const SNAP_NODE = 'snap_node'

/**
 * 智能参考线样式
 */
const guideConfig = {
  borderStroke: '#f5222d',
  borderStrokeWidth: 1,
}

enum Snap {
  'start',
  'center',
  'end',
}

enum Orientation {
  'V',
  'H',
}

interface SnappingEdge {
  guide: number
  offset: number
  snap: Snap
}

interface GuideStops {
  vertical: number[]
  horizontal: number[]
}

interface SnappingEdges {
  vertical: SnappingEdge[]
  horizontal: SnappingEdge[]
}

interface Guide {
  lineGuide: number
  orientation: Orientation
  snap: Snap
  offset: number
}

type SnappingEdgesType =
  | 'vertical-start'
  | 'vertical-center'
  | 'vertical-end'
  | 'horizontal-start'
  | 'horizontal-center'
  | 'horizontal-end'

interface Options {
  enableOnResize?: boolean
  snappingEdges?: SnappingEdgesType[]
}

// konav 参考线demo文档 https://konvajs.org/docs/sandbox/Objects_Snapping.html
// 给元件加snap_node的name就可以计算参考线了

export class Snapping {
  private _stage: Konva.Stage
  private _layer: Konva.Layer
  private _tr?: Konva.Transformer
  private _back?: Konva.Rect
  // 是否来自于transformer的事件
  private _isFromTransformer = false
  /** 处于transformer状态下缩放时是否启用snapping  */
  enableOnResize = false
  private _isTransformerResize = false
  /** 吸附轴线 */
  snappingEdges: SnappingEdgesType[] = [
    'vertical-start',
    'vertical-center',
    'vertical-end',
    'horizontal-start',
    'horizontal-center',
    'horizontal-end',
  ]

  constructor(stage: Konva.Stage, layer: Konva.Layer, option?: Options) {
    this._stage = stage
    this._layer = layer

    this._tr = stage.findOne('Transformer')
    this._back = this._tr?.findOne('.back')

    if (option) {
      this.enableOnResize = option.enableOnResize ?? false

      if (option.snappingEdges) {
        this.snappingEdges = option.snappingEdges
      }
    }

    this._handleDragMove = this._handleDragMove.bind(this)

    this._bindTransformerEvent()
    this._init()
  }

  private _bindTransformerEvent(): void {
    this._tr?.on('dragstart', () => {
      this._isFromTransformer = true
    })
    this._tr?.on('dragend', () => {
      this._isFromTransformer = false
    })
    this._tr?.on('transformstart', (e) => {
      this._isTransformerResize = this._tr?._movingAnchorName !== 'rotater'
    })
    this._tr?.on('transform', (e) => {
      if (this.enableOnResize && this._isTransformerResize) {
        this._handleDragMove(e)
      }
    })
    this._tr?.on('transformend', (e) => {
      this._isTransformerResize = false
      if (this.enableOnResize) {
        this._destroyLine()
      }
    })
  }

  private _init(): void {
    this._stage.on('dragmove', this._handleDragMove)

    this._stage.on('dragend', () => {
      this._destroyLine()
    })
  }

  private _destroyLine() {
    // clear all previous lines on the screen
    this._layer.find(`.${GUID_LINE}`).forEach((l) => l.destroy())
  }

  private _handleDragMove(e: Konva.KonvaEventObject<DragEvent>): void {
    if (!this._isFromTransformer && e.target instanceof Konva.Transformer) {
      return
    }

    if (this._isFromTransformer && !(e.target instanceof Konva.Transformer)) {
      return
    }

    const target = this._isFromTransformer
      ? (this._back as Konva.Rect)
      : (e.target as Konva.Rect)

    // clear all previous lines on the screen
    this._destroyLine()

    // find possible snapping lines
    const lineGuideStops = this._getLineGuideStops(target)
    // find snapping points of current object
    const itemBounds = this._getObjectSnappingEdges(target)

    // now find where can we snap current object
    const guides = this._getGuides(lineGuideStops, itemBounds)

    // do nothing of no snapping
    if (!guides.length) return

    this._drawGuides(guides)

    // capture the current position of the transformer
    const orgAbsPos = e.target.absolutePosition()
    const absPos = e.target.absolutePosition()

    guides.forEach((lg) => {
      switch (lg.snap) {
        case Snap.start: {
          switch (lg.orientation) {
            case Orientation.V: {
              absPos.x = lg.lineGuide + lg.offset
              break
            }
            case Orientation.H: {
              absPos.y = lg.lineGuide + lg.offset
              break
            }
            default:
              break
          }
          break
        }
        case Snap.center: {
          switch (lg.orientation) {
            case Orientation.V: {
              absPos.x = lg.lineGuide + lg.offset
              break
            }
            case Orientation.H: {
              absPos.y = lg.lineGuide + lg.offset
              break
            }
            default:
              break
          }
          break
        }
        case Snap.end: {
          switch (lg.orientation) {
            case Orientation.V: {
              absPos.x = lg.lineGuide + lg.offset
              break
            }
            case Orientation.H: {
              absPos.y = lg.lineGuide + lg.offset
              break
            }
            default:
              break
          }
          break
        }
        default:
          break
      }
    })

    // resize时不吸附位置
    if (this._isTransformerResize) return

    // gets the difference between the previous position and the new position
    const vecDiff = {
      x: orgAbsPos.x - absPos.x,
      y: orgAbsPos.y - absPos.y,
    }

    const nodes = this._isFromTransformer ? this._tr!.nodes() : [e.target]

    // apply the difference to each shape in the transformer.
    nodes.forEach((n) => {
      const nodeAbsPos = n.getAbsolutePosition()

      const newPos = {
        x: nodeAbsPos.x - vecDiff.x,
        y: nodeAbsPos.y - vecDiff.y,
      }

      n.setAbsolutePosition(newPos)
    })
  }

  private _drawGuides(guides: Guide[]): void {
    guides.forEach((lg) => {
      if (lg.orientation === Orientation.H) {
        const line = new Konva.Line({
          points: [-6000, 0, 6000, 0],
          stroke: guideConfig.borderStroke,
          strokeWidth: guideConfig.borderStrokeWidth,
          name: GUID_LINE,
        })
        this._layer.add(line)
        line.absolutePosition({
          x: 0,
          y: lg.lineGuide,
        })
      } else if (lg.orientation === Orientation.V) {
        const line = new Konva.Line({
          points: [0, -6000, 0, 6000],
          stroke: guideConfig.borderStroke,
          strokeWidth: guideConfig.borderStrokeWidth,
          name: GUID_LINE,
        })
        this._layer.add(line)
        line.absolutePosition({
          x: lg.lineGuide,
          y: 0,
        })
      }
    })
  }

  private _getGuides(
    lineGuideStops: GuideStops,
    itemBounds: SnappingEdges,
  ): Guide[] {
    const resultV: Array<Omit<Guide, 'orientation'> & { diff: number }> = []
    const resultH: Array<Omit<Guide, 'orientation'> & { diff: number }> = []

    lineGuideStops.vertical.forEach((lineGuide) => {
      itemBounds.vertical.forEach((itemBound) => {
        const diff = Math.abs(lineGuide - itemBound.guide)
        // if the distance between guild line and object snap point is close we can consider this for snapping
        if (diff < GUIDELINE_OFFSET) {
          resultV.push({
            lineGuide: lineGuide,
            diff: diff,
            snap: itemBound.snap,
            offset: itemBound.offset,
          })
        }
      })
    })

    lineGuideStops.horizontal.forEach((lineGuide) => {
      itemBounds.horizontal.forEach((itemBound) => {
        const diff = Math.abs(lineGuide - itemBound.guide)
        if (diff < GUIDELINE_OFFSET) {
          resultH.push({
            lineGuide: lineGuide,
            diff: diff,
            snap: itemBound.snap,
            offset: itemBound.offset,
          })
        }
      })
    })

    const guides: Array<{
      lineGuide: number
      offset: number
      orientation: Orientation
      snap: number
    }> = []

    // find closest snap
    const minV = resultV.sort((a, b) => a.diff - b.diff)[0]
    const minH = resultH.sort((a, b) => a.diff - b.diff)[0]
    if (minV) {
      guides.push({
        lineGuide: minV.lineGuide,
        offset: minV.offset,
        orientation: Orientation.V,
        snap: minV.snap,
      })
    }
    if (minH) {
      guides.push({
        lineGuide: minH.lineGuide,
        offset: minH.offset,
        orientation: Orientation.H,
        snap: minH.snap,
      })
    }
    return guides
  }

  private _getObjectSnappingEdges(node: Konva.Rect): SnappingEdges {
    const box = node.getClientRect({ skipStroke: node === this._back })

    const absPos = node.absolutePosition()

    const vertical = []
    const horizontal = []

    if (this.snappingEdges.includes('vertical-start')) {
      vertical.push({
        guide: Math.round(box.x),
        offset: Math.round(absPos.x - box.x),
        snap: Snap.start,
      })
    }
    if (this.snappingEdges.includes('vertical-center')) {
      vertical.push({
        guide: Math.round(box.x + box.width / 2),
        offset: Math.round(absPos.x - box.x - box.width / 2),
        snap: Snap.center,
      })
    }
    if (this.snappingEdges.includes('vertical-end')) {
      vertical.push({
        guide: Math.round(box.x + box.width),
        offset: Math.round(absPos.x - box.x - box.width),
        snap: Snap.end,
      })
    }

    if (this.snappingEdges.includes('horizontal-start')) {
      horizontal.push({
        guide: Math.round(box.y),
        offset: Math.round(absPos.y - box.y),
        snap: Snap.start,
      })
    }
    if (this.snappingEdges.includes('horizontal-center')) {
      horizontal.push({
        guide: Math.round(box.y + box.height / 2),
        offset: Math.round(absPos.y - box.y - box.height / 2),
        snap: Snap.center,
      })
    }
    if (this.snappingEdges.includes('horizontal-end')) {
      horizontal.push({
        guide: Math.round(box.y + box.height),
        offset: Math.round(absPos.y - box.y - box.height),
        snap: Snap.end,
      })
    }

    return {
      vertical,
      horizontal,
    }
  }

  private _getLineGuideStops(skipShape: Konva.Rect): GuideStops {
    // const ghostBox = this._ghostLayer.getClientRect()
    const vertical: number[] = [
      // ghostBox.x,
      // ghostBox.x + ghostBox.width,
      // ghostBox.x + ghostBox.width / 2,
    ]
    const horizontal: number[] = [
      // ghostBox.y,
      // ghostBox.y + ghostBox.height,
      // ghostBox.y + ghostBox.height / 2,
    ]

    // and we snap over edges and center of each object on the canvas
    const snapNodes = this._isTransformerResize
      ? this._layer.find(`.${SNAP_NODE}`)
      : this._stage.find(`.${SNAP_NODE}`)

    snapNodes
      .filter((t) => t.visible())
      .forEach((guideItem) => {
        if (
          guideItem === skipShape ||
          (this._isFromTransformer && this._tr!.nodes().includes(guideItem))
        ) {
          return
        }
        const box = guideItem.getClientRect()
        // and we can snap to all edges of shapes
        if (this.snappingEdges.includes('vertical-start')) {
          vertical.push(box.x)
        }
        if (this.snappingEdges.includes('vertical-center')) {
          vertical.push(box.x + box.width / 2)
        }
        if (this.snappingEdges.includes('vertical-end')) {
          vertical.push(box.x + box.width)
        }
        if (this.snappingEdges.includes('horizontal-start')) {
          horizontal.push(box.y)
        }
        if (this.snappingEdges.includes('horizontal-center')) {
          horizontal.push(box.y + box.height / 2)
        }
        if (this.snappingEdges.includes('horizontal-end')) {
          horizontal.push(box.y + box.height)
        }
      })
    return {
      vertical,
      horizontal,
    }
  }
}
